import { useQuery, useMutation, useQueryClient } from 'react-query';
import api, { 
  companyAPI, 
  servicesAPI, 
  productsAPI, 
  valuesAPI, 
  processAPI, 
  portfolioAPI, 
  teamAPI, 
  testimonialsAPI, 
  technologiesAPI, 
  contactAPI, 
  authAPI, 
  uploadAPI, 
  blogAPI, 
  jobsAPI, 
  chatAPI,
  dashboardAPI 
} from '../services/api';
import { normalizeFeatures } from '../utils/helpers';
import publicAPI from '../services/publicAPI';
import toast from 'react-hot-toast';

// Company hooks - using public API for better caching and no auth
export const useCompanyInfo = () => {
  return useQuery('companyInfo', () => publicAPI.getCompanyInfo().then(res => res.data.data), {
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
};

export const useCompanyStats = () => {
  return useQuery('companyStats', () => publicAPI.getCompanyStats().then(res => res.data.data), {
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
};

// Services hooks
export const useServices = (params = {}) => {
  return useQuery(['services', params], () => 
    servicesAPI.getAll(params).then(res => res.data.data), {
    staleTime: 0, // Always fetch fresh data
    cacheTime: 0, // Don't cache
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};

export const useService = (id) => {
  return useQuery(['service', id], () => 
    servicesAPI.getById(id).then(res => res.data.data), {
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useServiceBySlug = (slug) => {
  return useQuery(['service', slug], () => 
    servicesAPI.getBySlug(slug).then(res => res.data.data), {
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
};

// Products hooks
export const useProducts = (params = {}) => {
  return useQuery(['products', params], () => 
    productsAPI.getAll(params).then(res => {
      // Normalize features for each product
      const products = res.data.data || [];
      return products.map(product => ({
        ...product,
        features: normalizeFeatures(product.features)
      }));
    }), {
    staleTime: 5 * 60 * 1000,
  });
};

// Values hooks
export const useValues = () => {
  return useQuery('values', () => valuesAPI.getAll().then(res => res.data.data), {
    staleTime: 10 * 60 * 1000,
  });
};

// Process steps hooks
export const useProcessSteps = () => {
  return useQuery('processSteps', () => processAPI.getAll().then(res => res.data.data), {
    staleTime: 10 * 60 * 1000,
  });
};

// Portfolio hooks
export const usePortfolio = (params = {}) => {
  return useQuery(['portfolio', params], () => 
    portfolioAPI.getAll(params).then(res => res.data.data), {
    staleTime: 5 * 60 * 1000,
  });
};

// Team hooks
export const useTeam = (params = {}) => {
  return useQuery(['team', params], () => teamAPI.getAll(params).then(res => res.data.data), {
    staleTime: 10 * 60 * 1000,
  });
};

// Testimonials hooks
export const useTestimonials = (params = {}) => {
  return useQuery(['testimonials', params], () => 
    testimonialsAPI.getAll(params).then(res => res.data.data), {
    staleTime: 5 * 60 * 1000,
  });
};

// Technologies hooks
export const useTechnologies = (params = {}) => {
  return useQuery(['technologies', params], () => 
    technologiesAPI.getAll(params).then(res => res.data.data), {
    staleTime: 10 * 60 * 1000,
  });
};

// Contact form submission hook
export const useContactSubmit = () => {
  return useMutation(contactAPI.submit, {
    onSuccess: () => {
      toast.success('Message sent successfully! We will get back to you soon.');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to send message. Please try again.');
    },
  });
};

// Mutation hooks for admin operations
export const useUpdateCompanyInfo = () => {
  const queryClient = useQueryClient();
  
  return useMutation(companyAPI.updateInfo, {
    onSuccess: () => {
      queryClient.invalidateQueries('companyInfo');
      toast.success('Company information updated successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update company information.');
    },
  });
};

export const useCreateService = () => {
  const queryClient = useQueryClient();
  
  return useMutation(servicesAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('services');
      toast.success('Service created successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create service.');
    },
  });
};

export const useUpdateService = () => {
  const queryClient = useQueryClient();
  
  return useMutation(({ id, data }) => servicesAPI.update(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries('services');
      toast.success('Service updated successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update service.');
    },
  });
};

export const useDeleteService = () => {
  const queryClient = useQueryClient();
  
  return useMutation(servicesAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('services');
      toast.success('Service deleted successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete service.');
    },
  });
};

// Blog hooks
export const useBlogPosts = (params = {}) => {
  return useQuery(['blogPosts', params], () => 
    blogAPI.getAll(params).then(res => res.data.blogPosts), {
    staleTime: 5 * 60 * 1000,
  });
};

export const useBlogPost = (slug) => {
  return useQuery(['blogPost', slug], () => 
    blogAPI.getBySlug(slug).then(res => res.data), {
    staleTime: 5 * 60 * 1000,
    enabled: !!slug,
  });
};

export const useFeaturedBlogPosts = (limit = 3) => {
  return useQuery(['featuredBlogPosts', limit], () => 
    blogAPI.getFeatured(limit).then(res => res.data), {
    staleTime: 10 * 60 * 1000,
  });
};

export const useBlogCategories = () => {
  return useQuery('blogCategories', () => 
    blogAPI.getCategories().then(res => res.data), {
    staleTime: 30 * 60 * 1000,
  });
};

// Jobs hooks
export const useJobs = (params = {}) => {
  return useQuery(['jobs', params], () => 
    jobsAPI.getAll(params).then(res => res.data.jobs), {
    staleTime: 5 * 60 * 1000,
  });
};

export const useJob = (slug) => {
  return useQuery(['job', slug], () => 
    jobsAPI.getBySlug(slug).then(res => res.data), {
    staleTime: 5 * 60 * 1000,
    enabled: !!slug,
  });
};

export const useJobDepartments = () => {
  return useQuery('jobDepartments', () => 
    jobsAPI.getDepartments().then(res => res.data), {
    staleTime: 30 * 60 * 1000,
  });
};

export const useJobApplication = () => {
  return useMutation(({ jobId, data }) => 
    jobsAPI.apply(jobId, data), {
    onSuccess: () => {
      toast.success('Application submitted successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to submit application.');
    },
  });
};

// Dashboard hooks
export const useDashboardStats = () => {
  return useQuery('dashboardStats', () => 
    dashboardAPI.getStats().then(res => res.data.data), {
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 60 * 1000, // Refetch every minute
  });
};

export const useDashboardActivity = (limit = 10) => {
  return useQuery(['dashboardActivity', limit], () => 
    dashboardAPI.getActivity(limit).then(res => res.data.data), {
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });
};

export const useDashboardCharts = (days = 30) => {
  return useQuery(['dashboardCharts', days], () => 
    dashboardAPI.getChartData(days).then(res => res.data.data), {
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};